// Applies the `ui` directive a Director tool returns. Two shapes are accepted, alone or as a list:
//   the web brief's  {navigate?, playAssetId?, openUrl?, refresh?: string[]}
//   core's typed     {type: "navigate"|"open_project"|"play_video"|"open_checkout"|"open_packs"|"refresh"
//                     |"show_artifact"|"switch_director_voice"|"stage", ...}
import { api } from "./api";
import { pauseAfterLine, switchVoice } from "./director";
import { modelsPath, navigatePath, newVideoRequest, parseUi } from "./directives";
import { FORMAT_IDS } from "./formats";
import { applyHighlight } from "./highlight";
import { pausePlayback, playDirective, resumePlayback } from "./player";
import { applyStageDirective, backToStage, minimiseStage, stageBack } from "./stage/actions";
import { openUploadSheet } from "./components/UploadHost";
import { mountedFolds, resolveFoldId, setFold } from "./fold";
import { navigate } from "./router";
import { openPacks, packsFromDirective } from "./packs";
import { showDraft } from "./drafts";
import { openShare } from "./share";
import { bump, get, openNewVideo, showArtifact, toast } from "./store";
import type { UiDirective, UiFlat, UiTyped } from "./types";

/** refresh keys from the API ("channels", "me"...) mapped onto the keys screens listen to */
const REFRESH: Record<string, string[]> = {
  project: ["project", "projects"],
  projects: ["projects"],
  credits: ["credits"],
  library: ["library"],
  channels: ["brand", "me"],
  brand: ["brand", "me"],
  me: ["me", "credits"],
};

export async function applyUi(ui: UiDirective | undefined | null) {
  for (const p of parseUi(ui)) {
    if (p.kind === "typed") await applyTyped(p.d);
    else await applyFlat(p.d);
  }
}

async function applyFlat(ui: UiFlat) {
  if (ui.refresh?.length) bump(...ui.refresh.flatMap((k) => REFRESH[k] ?? [k]));
  if (ui.navigate) navigate(ui.navigate);
  if (ui.playAssetId) await play(ui.playAssetId);
  if (ui.openUrl) openUrl(ui.openUrl);
}

async function applyTyped(ui: UiTyped) {
  switch (ui.type) {
    case "navigate":
      if (ui.to === "stage") backToStage();
      else {
        navigate(navigatePath(ui));
        // v4 (T5): the review screen is its own player, so the stage steps aside to the call banner (a later `stage`
        // directive, like a note's options, brings it back)
        if (ui.to === "review" && get().stage.open && !get().stage.minimised) minimiseStage();
      }
      break;
    case "open_project":
      navigate(`/p/${ui.projectId}`);
      break;
    case "play_video":
      // v2 shape: treated like v2.1's `play` (the Director's line, then the app plays it)
      if (ui.url) playDirective({ type: "play", what: "render", projectId: ui.projectId ?? "", assetId: ui.assetId ?? "", url: ui.url, then: "ask" });
      else if (ui.assetId) await play(ui.assetId);
      break;
    case "play":
      playDirective(ui);
      break;
    case "needs_review":
      // the tray reads project state; make sure it's fresh
      bump("project", "projects");
      break;
    case "open_checkout":
      openUrl(ui.url);
      break;
    // Wave 3 billing: the credit packs inline over the current screen (components/PacksSheet.tsx)
    case "open_packs":
      openPacks(packsFromDirective(ui));
      break;
    case "refresh":
      bump(...(REFRESH[ui.what] ?? [ui.what]));
      break;
    case "show_artifact":
      showArtifact(ui.artifact);
      break;
    // v3: "what can you make?" shows the type cards; "make a documentary" opens its brief
    case "show_formats":
    case "new_video_sheet": {
      const req = newVideoRequest(ui, FORMAT_IDS);
      if (req) openNewVideo(req);
      break;
    }
    case "show_models":
      navigate(modelsPath(ui.step));
      break;
    // v4 (V4-SH): the share sheet for a version (its links, Copy, Turn off, what viewers said)
    case "share_sheet":
      openShare({ projectId: ui.projectId, version: ui.version, linkId: ui.linkId });
      break;
    case "memory_changed":
      bump("memory");
      break;
    // v4: fill the Director's stage (and bring the user back to it, unless they're typing; src/stage)
    case "stage":
      applyStageDirective(ui);
      break;
    // v4 (V4-D3 add_files): the upload sheet at a level (or a video's Suno pack); the user taps to pick files
    case "upload_sheet":
      openUploadSheet(ui);
      break;
    // v4 (V4-D4 show_references, D5 fold): open or close a fold by id; with a video, go to it (on that scene) when
    // no call stage covers the screen (during a call the stage shows it, and the fold is open when they get there)
    case "fold": {
      // D5: a bare section name ("versions") means that fold on the screen they're looking at
      const m = mountedFolds();
      const st = get().stage;
      setFold(resolveFoldId(ui.id, m.all, m.stage, st.open && !st.minimised), ui.open);
      if (ui.open && ui.projectId && !get().stage.open) {
        const here = typeof location !== "undefined" && location.pathname.startsWith(`/p/${ui.projectId}`);
        if (!here || ui.sceneIndex) navigate(`/p/${ui.projectId}${ui.sceneIndex ? `?scene=${ui.sceneIndex}` : ""}`);
      }
      break;
    }
    // 2026-11-03: the Director paused the call: end it once its line is done (the conversation carries over)
    case "pause_call":
      void pauseAfterLine();
      break;
    // v4 (V4-D5): the pause / resume tools do what the player's buttons do (a play_and_wait keeps waiting)
    case "playback":
      if (ui.action === "pause") pausePlayback();
      else resumePlayback();
      break;
    // v4 (V4-D5): `back`: the stage's previous items, else the previous screen
    case "back": {
      const st = get().stage;
      if (st.open && st.history.length) stageBack();
      else if (st.open && st.minimised) backToStage();
      else if (typeof history !== "undefined" && history.length > 1) history.back();
      break;
    }
    // v4 (D7, how_do_i): go there and flash the control (not awaited: it waits for the screen to draw)
    case "highlight":
      void applyHighlight(ui);
      break;
    // 2026-11-05 (feedback pipeline): the Director's draft report, on screen for the user to Send / Edit / Discard
    case "feedback_draft":
      showDraft(ui);
      break;
    case "switch_director_voice":
      // not awaited: the tool result must reach the agent before the session it came from is ended
      setTimeout(() => void switchVoice(ui.voiceId, ui.voiceName), 250);
      break;
  }
}

/** Same-origin paths stay in the app; anything else (Stripe Checkout) is a full redirect. */
export function openUrl(url: string) {
  try {
    const u = new URL(url, location.origin);
    if (u.origin === location.origin && !u.pathname.startsWith("/api/")) navigate(u.pathname + u.search);
    else location.assign(u.href);
  } catch {
    toast("The Director sent a link the app couldn't open.", "bad");
  }
}

/** Find a signed URL for a video asset id: a live render first, then the library. */
export async function resolveAssetUrl(assetId: string): Promise<string | null> {
  for (const r of Object.values(get().renders)) if (r.videoAssetId === assetId && r.videoUrl) return r.videoUrl;
  try {
    const items = await api.library({ kind: "video" });
    const hit = items.find((a) => a.id === assetId);
    if (hit?.url) return hit.url;
  } catch {
    /* fall through */
  }
  return null;
}

export async function play(assetId: string, title?: string) {
  const url = await resolveAssetUrl(assetId);
  if (!url) {
    toast("That video isn't ready to play yet.", "bad");
    return;
  }
  playDirective({ type: "play", what: "render", projectId: "", assetId, url, title, then: "ask" });
}
